import { z } from "zod/v4";
import { endOfDay as endOfDayDate } from "date-fns";
import { ActivityKind, ActivityEdgeEventState } from "@visioni/a3s-activity-recorder/types";
import { TenantPathParams, defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";

// ---------------------------------------------------------------------------
// Path params — tenant-scoped (activity spans sites/cameras)
// ---------------------------------------------------------------------------

export type ListActivityPathParams = z.output<typeof TenantPathParams>;

// ---------------------------------------------------------------------------
// Query params — filters and pagination
// ---------------------------------------------------------------------------

export const ListActivityQuery = z.object({
  siteId: z.string().optional(),
  cameraKey: z.string().optional(),
  kinds: z.string().optional(),
  userEmail: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  limit: z.string().optional(),
  offset: z.string().optional(),
});
export type ListActivityQuery = z.output<typeof ListActivityQuery>;

export interface ParsedListActivityQuery {
  siteId?: string | undefined;
  cameraKey?: string | undefined;
  kinds?: string | undefined;
  userEmail?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  limit: number;
  offset: number;
}

export function parseListQuery(raw: ListActivityQuery): ParsedListActivityQuery {
  return {
    ...raw,
    dateTo: raw.dateTo ? endOfDay(raw.dateTo) : undefined,
    limit: Math.min(Math.max(parseInt(raw.limit ?? "50", 10) || 50, 1), 200),
    offset: Math.max(parseInt(raw.offset ?? "0", 10) || 0, 0),
  };
}

/** If a date-only string (YYYY-MM-DD), return the last instant of that day. */
function endOfDay(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return endOfDayDate(value).toISOString();
  }
  return value;
}

// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------

export const ActivityEdgeEventResponse = z.object({
  state: ActivityEdgeEventState,
  at: z.iso.datetime(),
  reason: z.string().nullable(),
});
export type ActivityEdgeEventResponse = z.output<typeof ActivityEdgeEventResponse>;

export const ActivityEdgeResponse = z.object({
  status: z.string(),
  statusUpdatedAt: z.iso.datetime(),
  statusReason: z.string().nullable(),
  correlationKey: z.string(),
  events: z.array(ActivityEdgeEventResponse),
});
export type ActivityEdgeResponse = z.output<typeof ActivityEdgeResponse>;

export const ActivityListItem = z.object({
  id: z.string(),
  kind: ActivityKind,
  summary: z.string(),
  actorId: z.string().nullable(),
  actorEmail: z.string().nullable(),
  siteId: z.string().nullable(),
  cameraId: z.string().nullable(),
  assetId: z.string().nullable(),
  edge: ActivityEdgeResponse.nullable(),
  data: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.iso.datetime(),
});
export type ActivityListItem = z.output<typeof ActivityListItem>;

export const ListActivityResponse = z.object({
  data: z.array(ActivityListItem),
  totalCount: z.number().int().nonnegative(),
});
export type ListActivityResponse = z.output<typeof ListActivityResponse>;

// ---------------------------------------------------------------------------
// Resource & endpoint definitions
// ---------------------------------------------------------------------------

export const activityListResource = defineResource({
  feature: FEATURE_NAME,
  resource: "items",
  scope: "tenant",
});

export const listActivityEndpoint = defineEndpoint(activityListResource, {
  responseSchema: ListActivityResponse,
  querySchema: ListActivityQuery,
});

export const myActivityListResource = defineResource({
  feature: FEATURE_NAME,
  resource: "items/me",
  scope: "tenant",
});

export const listMyActivityEndpoint = defineEndpoint(myActivityListResource, {
  responseSchema: ListActivityResponse,
  querySchema: ListActivityQuery,
});
